import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Shield } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ProfileMenu } from "@/components/profile/ProfileMenu";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle, ThemeToggleIconButton } from "@/components/ThemeToggle";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { prefetchRoute } from "@/lib/prefetchRoute";

interface NavigationProps {
  /** When true, hides the centered marketing menu (Home/Courses/Pricing/About/Blog/Contact)
   *  and uses a relative position. Used inside DashboardShell where the sidebar carries primary nav. */
  compact?: boolean;
}

const Navigation = ({ compact = false }: NavigationProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const { isAdmin, isInstructor } = useUserRole();
  const { t } = useTranslation();

  return (
    <nav
      className={
        compact
          ? "relative w-full bg-transparent"
          : "fixed top-0 left-0 right-0 z-50 bg-card/40 backdrop-blur-2xl shadow-glass border-b border-white/10 pt-safe pl-safe pr-safe"
      }
      role="navigation"
      aria-label="Main navigation"
    >
      <div className={compact ? "px-4 lg:px-6 max-w-full" : "container mx-auto px-4 lg:px-8 max-w-full"}>
        <div className={compact
          ? "flex items-center justify-end gap-4 h-14"
          : "flex lg:grid lg:grid-cols-[auto_1fr_auto] items-center justify-between lg:gap-4 h-16"
        }>
          {/* Logo - hidden in compact (sidebar carries the brand) */}
          {!compact && (
            <Link
              to="/"
              className="flex items-center group"
              aria-label="AICAVITE - Home"
            >
              <span
                className="text-xl font-semibold tracking-[0.22em] bg-gradient-primary bg-clip-text text-transparent group-hover:opacity-90 transition-opacity"
                aria-hidden="true"
              >
                AICAVITE
              </span>
            </Link>
          )}

          {/* Desktop Navigation - Centered (hidden in compact) */}
          {!compact && (
            <div className="hidden lg:flex items-center justify-center gap-6" role="menubar">
              <Link
                to="/"
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.home")}
              </Link>
              <Link
                to="/courses"
                onMouseEnter={() => prefetchRoute("courses")}
                onFocus={() => prefetchRoute("courses")}
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.courses")}
              </Link>
              <Link
                to="/pricing"
                onMouseEnter={() => prefetchRoute("pricing")}
                onFocus={() => prefetchRoute("pricing")}
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.pricing")}
              </Link>
              <Link
                to="/about"
                onMouseEnter={() => prefetchRoute("about")}
                onFocus={() => prefetchRoute("about")}
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.about")}
              </Link>
              <Link
                to="/blog"
                onMouseEnter={() => prefetchRoute("blog")}
                onFocus={() => prefetchRoute("blog")}
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.blog")}
              </Link>
              <Link
                to="/contact"
                onMouseEnter={() => prefetchRoute("contact")}
                onFocus={() => prefetchRoute("contact")}
                className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                role="menuitem"
              >
                {t("navigation.contact")}
              </Link>
            </div>
          )}

          {/* Auth Buttons - Fixed Right */}
          <div className={cn(
            "hidden lg:flex items-center justify-end gap-2 flex-shrink-0",
            compact ? "w-auto pr-2" : "w-auto"
          )}>
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="h-9 w-32 rounded-full bg-muted animate-pulse" />
                <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
              </div>
            ) : user ? (
              <>
                {/* Unified glass control group: theme | language | coin | bell */}
                <div className="flex items-center h-9 rounded-full glass-surface glass-border px-1 gap-0">
                  <ThemeToggleIconButton />
                  <span className="w-px h-5 bg-foreground/10 mx-0.5" aria-hidden="true" />
                  <LanguageSwitcher compact />
                  <span className="w-px h-5 bg-foreground/10 mx-0.5" aria-hidden="true" />
                  <div className="flex items-center justify-center h-7 w-7 [&>button]:!h-7 [&>button]:!w-7">
                    <NotificationBell />
                  </div>
                </div>
                <ProfileMenu user={user} />
              </>
            ) : (
              <>
                <div className="flex items-center h-9 rounded-full glass-surface glass-border px-1 gap-0">
                  <ThemeToggleIconButton />
                  <span className="w-px h-5 bg-foreground/10 mx-0.5" aria-hidden="true" />
                  <LanguageSwitcher compact />
                </div>
                <Link to="/auth">
                  <Button variant="ghost" size="default">
                    {t("navigation.login")}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="default" size="default">
                    {t("navigation.signUp")}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Auth & Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            {loading ? (
              <div className="w-9 h-9 rounded-full bg-muted animate-pulse" />
            ) : user ? (
              <>
                <div className="flex items-center h-9 rounded-full glass-surface glass-border px-1 gap-0">
                  <LanguageSwitcher compact />
                  <span className="w-px h-5 bg-foreground/10 mx-0.5" aria-hidden="true" />
                  <div className="flex items-center justify-center h-7 [&>button]:!h-7 [&>button]:!w-7">
                    <NotificationBell />
                  </div>
                </div>
                <ProfileMenu user={user} />
              </>
            ) : (
              <LanguageSwitcher />
            )}
            {!compact && (
              <button
                className="text-foreground p-2 hover:bg-muted/30 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu with proper spacing */}
        {mobileMenuOpen && !compact && (
          <div
            className="lg:hidden py-6 space-y-2 border-t border-white/10 bg-card/20 backdrop-blur-xl rounded-b-2xl animate-slide-in-from-top"
            role="menu"
          >
            <Link
              to="/"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              role="menuitem"
            >
              {t("navigation.home")}
            </Link>
            <Link
              to="/courses"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.courses")}
            </Link>
            <Link
              to="/pricing"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.pricing")}
            </Link>
            <Link
              to="/about"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.about")}
            </Link>
            {user && (
              <Link
                to="/announcements"
                className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("navigation.announcements")}
              </Link>
            )}
            <Link
              to="/blog"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.blog")}
            </Link>
            <Link
              to="/contact"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.contact")}
            </Link>
            <Link
              to="/support"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.support")}
            </Link>
            <Link
              to="/faq"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.faq")}
            </Link>
            <div className="flex flex-col gap-3 pt-4 px-4 border-t border-white/10 mt-4">
              <div className="flex items-center justify-center pb-2">
                <ThemeToggle variant="full" />
              </div>
              {loading ? (
                <div className="w-full h-10 bg-muted animate-pulse rounded-lg" />
              ) : user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="default" size="default" className="w-full">
                    {t("navigation.dashboard")}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" size="default" className="w-full">
                      {t("navigation.login")}
                    </Button>
                  </Link>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="default" size="default" className="w-full">
                      {t("navigation.signUp")}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export { Navigation };
export default Navigation;
